import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  TextField,
  Card,
  CardContent,
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BuildIcon from '@mui/icons-material/Build';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StorageIcon from '@mui/icons-material/Storage';
import CloudIcon from '@mui/icons-material/Cloud';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import { directDocumentQuery, getSOWInsightsForPrepare } from '../../utils/aiService';
import { getTechnicalRequirements, saveTechnicalRequirements, fetchTechnicalRequirementsFromSOW } from '../../utils/preparePhaseService';

// Styled components
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
  },
}));

// Category Icons mapping
const categoryIcons = {
  infrastructure: <StorageIcon />,
  security: <SecurityIcon />,
  integration: <CloudIcon />,
  configuration: <SettingsIcon />
};

const TechnicalRequirementsPage = () => {
  const { projectId } = useParams();
  const { project } = useOutletContext();
  
  const [technicalReqs, setTechnicalReqs] = useState({
    overview: '',
    infrastructure: [],
    security: [],
    integration: [],
    configuration: []
  });
  
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [primarySource, setPrimarySource] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Edit states
  const [editMode, setEditMode] = useState(false);
  const [editingTechnicalReqs, setEditingTechnicalReqs] = useState({
    overview: '',
    infrastructure: [],
    security: [],
    integration: [],
    configuration: []
  });
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [dialogItem, setDialogItem] = useState({
    id: '',
    name: '',
    description: '',
    category: 'infrastructure',
    impact: 'medium',
    specification: ''
  });
  const [dialogItemIndex, setDialogItemIndex] = useState(null);
  
  // Load technical requirements from database when component mounts
  useEffect(() => {
    if (projectId) {
      loadTechnicalRequirementsFromDB();
    }
  }, [projectId]);
  
  // Load technical requirements from database
  const loadTechnicalRequirementsFromDB = async () => {
    setLoading(true);
    try {
      const requirementsData = await getTechnicalRequirements(projectId);
      
      if (requirementsData && requirementsData.overview !== undefined) {
        // We have data in the database
        setTechnicalReqs(requirementsData);
        setEditingTechnicalReqs(requirementsData);
      } else {
        // No data in database, set empty state
        const emptyRequirements = {
          overview: '',
          infrastructure: [],
          security: [],
          integration: [],
          configuration: []
        };
        setTechnicalReqs(emptyRequirements);
        setEditingTechnicalReqs(emptyRequirements);
      }
    } catch (error) {
      console.error('Error loading technical requirements from database:', error);
      // Set empty state on error
      const emptyRequirements = {
        overview: '',
        infrastructure: [],
        security: [],
        integration: [],
        configuration: []
      };
      setTechnicalReqs(emptyRequirements);
      setEditingTechnicalReqs(emptyRequirements);
    } finally {
      setLoading(false);
    }
  };
  
  
  // Get the SOW document from the project
  const getSowDocument = () => {
    if (project && project.documents && project.documents.sow && project.documents.sow.length > 0) {
      return project.documents.sow[0];
    }
    return null;
  };
  
  // Fetch requirements from project documents and save to database
  const fetchRequirementsFromSOW = async () => {
    if (!projectId) return;
    
    // Check if we have a SOW document
    const sowDoc = getSowDocument();
    if (!sowDoc) {
      setSnackbar({
        open: true,
        message: 'No project documents found. Please upload project documents first.',
        severity: 'warning'
      });
      return;
    }
    
    setAiLoading(true);
    setAiResponse(null);
    
    try {
      // Call the new backend endpoint to fetch from project documents and save to DB
      const result = await fetchTechnicalRequirementsFromSOW(projectId);
      
      if (result && result.data) {
        // Update the requirements state with the fetched and saved data
        setTechnicalReqs(result.data);
        setEditingTechnicalReqs(result.data);
        
        // Store source information
        if (result.source) {
          setPrimarySource(result.source);
        }
        
        setSnackbar({
          open: true,
          message: 'Technical requirements successfully fetched from project documents and saved.',
          severity: 'success'
        });
        
        // Automatically enter edit mode to allow user to review and modify
        setEditMode(true);
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to extract technical requirements from project documents.',
          severity: 'error'
        });
      }
    } catch (error) {
      console.error("Error fetching requirements from project documents:", error);
      setSnackbar({
        open: true,
        message: 'Error fetching requirements from project documents.',
        severity: 'error'
      });
    } finally {
      setAiLoading(false);
    }
  };
  
  
  // Enter edit mode
  const handleEdit = () => {
    setEditingTechnicalReqs({...technicalReqs});
    setEditMode(true);
  };
  
  // Cancel edit mode
  const handleCancel = () => {
    setEditingTechnicalReqs({...technicalReqs});
    setEditMode(false);
  };
  
  // Save changes to database
  const handleSave = async () => {
    setLoading(true);
    try {
      await saveTechnicalRequirements(projectId, editingTechnicalReqs);
      
      setTechnicalReqs({...editingTechnicalReqs});
      setEditMode(false);
      
      setSnackbar({
        open: true,
        message: 'Technical requirements saved successfully.',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving technical requirements:', error);
      setSnackbar({
        open: true,
        message: 'Error saving technical requirements. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Handle overview change
  const handleOverviewChange = (e) => {
    setEditingTechnicalReqs(prevState => ({
      ...prevState,
      overview: e.target.value
    }));
  };
  
  // Open add/edit dialog
  const handleOpenDialog = (type, index = null) => {
    setDialogType(type);
    setDialogItemIndex(index);
    
    if (index !== null) {
      // Edit existing item
      switch (type) {
        case 'infrastructure':
          setDialogItem({...editingTechnicalReqs.infrastructure[index]});
          break;
        case 'security':
          setDialogItem({...editingTechnicalReqs.security[index]});
          break;
        case 'integration':
          setDialogItem({...editingTechnicalReqs.integration[index]});
          break;
        case 'configuration':
          setDialogItem({...editingTechnicalReqs.configuration[index]});
          break;
        default:
          setDialogItem({
            id: '',
            name: '',
            description: '',
            category: type,
            impact: 'medium',
            specification: ''
          });
      }
    } else {
      // Add new item - generate a new ID based on type
      const newId = generateNewId(type);
      
      setDialogItem({
        id: newId,
        name: '',
        description: '',
        category: type,
        impact: 'medium',
        specification: ''
      });
    }
    
    setOpenDialog(true);
  };
  
  // Generate a new ID based on type
  const generateNewId = (type) => {
    let prefix = 'TECH';
    let categoryCode = type.substring(0, 3).toUpperCase();
    let existingIds = [];
    
    switch (type) {
      case 'infrastructure':
        categoryCode = 'INF';
        existingIds = editingTechnicalReqs.infrastructure.map(item => item.id);
        break;
      case 'security':
        categoryCode = 'SEC';
        existingIds = editingTechnicalReqs.security.map(item => item.id);
        break;
      case 'integration':
        categoryCode = 'INT';
        existingIds = editingTechnicalReqs.integration.map(item => item.id);
        break;
      case 'configuration':
        categoryCode = 'CONF';
        existingIds = editingTechnicalReqs.configuration.map(item => item.id);
        break;
      default:
        categoryCode = 'REQ';
    }
    
    // Find the highest existing ID number and increment by 1
    let highestNum = 0;
    existingIds.forEach(id => {
      const match = id.match(new RegExp(`^${prefix}-${categoryCode}-(\\d+)$`));
      if (match) {
        const num = parseInt(match[1], 10);
        if (num > highestNum) highestNum = num;
      }
    });
    
    // Format with leading zeros
    const nextNum = highestNum + 1;
    const paddedNum = nextNum.toString().padStart(3, '0');
    
    return `${prefix}-${categoryCode}-${paddedNum}`;
  };
  
  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setDialogItem({
      id: '',
      name: '',
      description: '',
      category: 'infrastructure',
      impact: 'medium',
      specification: ''
    });
  };
  
  // Handle dialog input change
  const handleDialogInputChange = (e) => {
    const { name, value } = e.target;
    setDialogItem(prevState => ({
      ...prevState,
      [name]: value
    }));
  };
  
  // Save dialog item
  const handleSaveDialogItem = () => {
    if (!dialogItem.name.trim() || !dialogItem.id.trim()) {
      return;
    }
    
    const updatedTechnicalReqs = {...editingTechnicalReqs};
    
    if (dialogItemIndex === null) {
      // Add new item
      switch (dialogType) {
        case 'infrastructure':
          updatedTechnicalReqs.infrastructure = [...updatedTechnicalReqs.infrastructure, {...dialogItem}];
          break;
        case 'security':
          updatedTechnicalReqs.security = [...updatedTechnicalReqs.security, {...dialogItem}];
          break;
        case 'integration':
          updatedTechnicalReqs.integration = [...updatedTechnicalReqs.integration, {...dialogItem}];
          break;
        case 'configuration':
          updatedTechnicalReqs.configuration = [...updatedTechnicalReqs.configuration, {...dialogItem}];
          break;
        default:
          break;
      }
    } else {
      // Edit existing item
      switch (dialogType) {
        case 'infrastructure':
          updatedTechnicalReqs.infrastructure[dialogItemIndex] = {...dialogItem};
          break;
        case 'security':
          updatedTechnicalReqs.security[dialogItemIndex] = {...dialogItem};
          break;
        case 'integration':
          updatedTechnicalReqs.integration[dialogItemIndex] = {...dialogItem};
          break;
        case 'configuration':
          updatedTechnicalReqs.configuration[dialogItemIndex] = {...dialogItem};
          break;
        default:
          break;
      }
    }
    
    setEditingTechnicalReqs(updatedTechnicalReqs);
    handleCloseDialog();
  };
  
  // Delete item
  const handleDeleteItem = (type, index) => {
    const updatedTechnicalReqs = {...editingTechnicalReqs};
    
    switch (type) {
      case 'infrastructure':
        updatedTechnicalReqs.infrastructure = updatedTechnicalReqs.infrastructure.filter((_, i) => i !== index);
        break;
      case 'security':
        updatedTechnicalReqs.security = updatedTechnicalReqs.security.filter((_, i) => i !== index);
        break;
      case 'integration':
        updatedTechnicalReqs.integration = updatedTechnicalReqs.integration.filter((_, i) => i !== index);
        break;
      case 'configuration':
        updatedTechnicalReqs.configuration = updatedTechnicalReqs.configuration.filter((_, i) => i !== index);
        break;
      default:
        break;
    }
    
    setEditingTechnicalReqs(updatedTechnicalReqs);
  };
  
  // Get impact color
  const getImpactColor = (impact) => {
    switch (impact.toLowerCase()) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };
  
  // Render requirements table
  const renderRequirementsTable = (items, type) => {
    return (
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell width="15%"><strong>ID</strong></TableCell>
              <TableCell width="25%"><strong>Name</strong></TableCell>
              <TableCell width="20%"><strong>Description</strong></TableCell>
              <TableCell width="10%"><strong>Impact</strong></TableCell>
              <TableCell width="20%"><strong>Specification</strong></TableCell>
              {editMode && <TableCell width="10%" align="center"><strong>Actions</strong></TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={index} hover>
                <TableCell>{item.id || ''}</TableCell>
                <TableCell>{item.name || ''}</TableCell>
                <TableCell>{item.description || ''}</TableCell>
                <TableCell>
                  <Chip
                    label={(item.impact || 'medium').toUpperCase()}
                    color={getImpactColor(item.impact || 'medium')}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                  {item.specification || ''}
                </TableCell>
                {editMode && (
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleOpenDialog(type, index)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteItem(type, index)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {editMode && (
              <TableRow>
                <TableCell colSpan={editMode ? 6 : 5} align="center">
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog(type)}
                    variant="outlined"
                    size="small"
                    sx={{ my: 1 }}
                  >
                    Add Requirement
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };
  
  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <SectionTitle variant="h5">
            <BuildIcon />
            Technical Requirements
          </SectionTitle>
          <Box>
            {editMode ? (
              <>
                <Button
                  startIcon={<SaveIcon />}
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  sx={{ mr: 1 }}
                >
                  Save
                </Button>
                <Button
                  startIcon={<CancelIcon />}
                  variant="outlined"
                  color="secondary"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  startIcon={<AutoAwesomeIcon />}
                  variant="contained"
                  color="primary"
                  onClick={fetchRequirementsFromSOW}
                  disabled={aiLoading || !getSowDocument()}
                  sx={{ mr: 1 }}
                >
                  {aiLoading ? 'Fetching...' : 'Fetch From Project Documents'}
                </Button>
                <Button
                  startIcon={<EditIcon />}
                  variant="outlined"
                  color="primary"
                  onClick={handleEdit}
                >
                  Edit
                </Button>
              </>
            )}
          </Box>
        </Box>
        
        <Typography variant="body1" paragraph>
          Define the technical requirements and specifications for the SAP implementation including infrastructure, security, integration, and configuration.
        </Typography>
        
        {!getSowDocument() && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            No project documents found. Upload project documents to enable AI-assisted content generation.
          </Alert>
        )}
        
        {primarySource && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Content generated from: {primarySource.filename || primarySource.document_name || 'Project Document'}
          </Alert>
        )}
        
        {aiLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {/* Overview */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Overview
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {editMode ? (
              <TextField
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                value={editingTechnicalReqs.overview}
                onChange={handleOverviewChange}
                placeholder="Enter an overview of the technical requirements"
              />
            ) : (
              <Typography variant="body1" paragraph>
                {technicalReqs.overview}
              </Typography>
            )}
          </CardContent>
        </Card>
        
        {/* Infrastructure Requirements */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <StorageIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="primary">
              Infrastructure Requirements
            </Typography>
          </Box>
          {renderRequirementsTable(
            editMode ? editingTechnicalReqs.infrastructure : technicalReqs.infrastructure,
            'infrastructure'
          )}
        </Box>
        
        {/* Security Requirements */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SecurityIcon color="error" sx={{ mr: 1 }} />
            <Typography variant="h6" color="error">
              Security Requirements
            </Typography>
          </Box>
          {renderRequirementsTable(
            editMode ? editingTechnicalReqs.security : technicalReqs.security,
            'security'
          )}
        </Box>
        
        {/* Integration Requirements */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <CloudIcon color="info" sx={{ mr: 1 }} />
            <Typography variant="h6" color="info.main">
              Integration Requirements
            </Typography>
          </Box>
          {renderRequirementsTable(
            editMode ? editingTechnicalReqs.integration : technicalReqs.integration,
            'integration'
          )}
        </Box>
        
        {/* Configuration Requirements */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <SettingsIcon color="secondary" sx={{ mr: 1 }} />
            <Typography variant="h6" color="secondary">
              Configuration Requirements
            </Typography>
          </Box>
          {renderRequirementsTable(
            editMode ? editingTechnicalReqs.configuration : technicalReqs.configuration,
            'configuration'
          )}
        </Box>
      </Paper>
      
      {/* AI Response Accordion (if available) */}
      {aiResponse && (
        <Accordion sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography>AI-Generated Content (Raw Response)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Paper sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
              <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                {aiResponse}
              </Typography>
            </Paper>
          </AccordionDetails>
        </Accordion>
      )}
      
      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogItemIndex === null ? 'Add' : 'Edit'} {
            dialogType === 'infrastructure' ? 'Infrastructure Requirement' :
            dialogType === 'security' ? 'Security Requirement' :
            dialogType === 'integration' ? 'Integration Requirement' :
            'Configuration Requirement'
          }
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField
                name="id"
                label="Requirement ID"
                value={dialogItem.id}
                onChange={handleDialogInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="impact-label">Impact</InputLabel>
                <Select
                  labelId="impact-label"
                  name="impact"
                  value={dialogItem.impact}
                  onChange={handleDialogInputChange}
                  label="Impact"
                >
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Name"
                value={dialogItem.name}
                onChange={handleDialogInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                value={dialogItem.description}
                onChange={handleDialogInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="specification"
                label="Specification"
                value={dialogItem.specification}
                onChange={handleDialogInputChange}
                fullWidth
                variant="outlined"
                margin="dense"
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveDialogItem} 
            variant="contained" 
            disabled={!dialogItem.name || !dialogItem.id}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TechnicalRequirementsPage;
